import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const location = useLocation();
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [alertCounts, setAlertCounts] = useState({
    compliance: 2,
    news: 5
  });
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    { path: '/main-dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: '/market-analysis', label: 'Market Analysis', icon: 'TrendingUp' },
    { path: '/real-time-order-book', label: 'Order Book', icon: 'BookOpen' },
    { path: '/strategy-builder', label: 'Strategy Builder', icon: 'Settings' },
    { path: '/compliance-dashboard', label: 'Compliance', icon: 'Shield', alertCount: alertCounts?.compliance },
  ];

  const moreItems = [
    { path: '/historical-data-explorer', label: 'Historical Data', icon: 'Database' },
    { path: '/news-and-events', label: 'News & Events', icon: 'Newspaper', alertCount: alertCounts?.news },
  ];

  useEffect(() => {
    // Simulate connection status monitoring
    const interval = setInterval(() => {
      const statuses = ['connected', 'connecting', 'disconnected'];
      const randomStatus = statuses?.[Math.floor(Math.random() * statuses?.length)];
      setConnectionStatus(randomStatus);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const getConnectionColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-success';
      case 'connecting': return 'text-warning';
      case 'disconnected': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case 'connected': return 'Wifi';
      case 'connecting': return 'WifiOff';
      case 'disconnected': return 'WifiOff';
      default: return 'Wifi';
    }
  };

  const handleNavigation = (path) => {
    window.location.href = path;
    setIsMobileMenuOpen(false);
  };

  const handleProfileToggle = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const renderNavItem = (item, isMobile = false) => (
    <button
      key={item?.path}
      onClick={() => handleNavigation(item?.path)}
      className={`
        relative flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200
        ${location?.pathname === item?.path 
          ? 'bg-primary text-primary-foreground' 
          : 'text-foreground hover:bg-muted hover:text-foreground'
        }
        ${isMobile ? 'w-full justify-start' : ''}
      `}
    >
      <Icon name={item?.icon} size={16} />
      <span>{item?.label}</span>
      {item?.alertCount && item?.alertCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
          {item?.alertCount}
        </span>
      )}
    </button>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-subtle">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo */}
        <div className="flex items-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={20} color="white" />
            </div>
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold text-foreground">TradePulse</h1>
              <span className="text-xs text-muted-foreground font-medium">Pro</span>
            </div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {navigationItems?.map(item => renderNavItem(item))}
          
          {/* More Dropdown */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-2"
            >
              <Icon name="MoreHorizontal" size={16} />
              <span>More</span>
            </Button>
            
            {isProfileOpen && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-popover border border-border rounded-md shadow-elevated z-50">
                <div className="py-1">
                  {moreItems?.map(item => (
                    <button
                      key={item?.path}
                      onClick={() => handleNavigation(item?.path)}
                      className={`
                        relative w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors duration-200
                        ${location?.pathname === item?.path 
                          ? 'bg-accent text-accent-foreground' 
                          : 'text-popover-foreground hover:bg-muted'
                        }
                      `}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.label}</span>
                      {item?.alertCount && item?.alertCount > 0 && (
                        <span className="ml-auto bg-error text-error-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                          {item?.alertCount}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Right Section */}
        <div className="flex items-center gap-4">
          {/* Connection Status */}
          <div className="hidden sm:flex items-center gap-2">
            <Icon 
              name={getConnectionIcon()} 
              size={16} 
              className={`${getConnectionColor()} ${connectionStatus === 'connecting' ? 'animate-pulse' : ''}`}
            />
            <span className={`text-xs font-medium ${getConnectionColor()}`}>
              {connectionStatus?.charAt(0)?.toUpperCase() + connectionStatus?.slice(1)}
            </span>
          </div>

          {/* User Profile Dropdown */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleProfileToggle}
              className="hidden md:flex items-center gap-2"
            >
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                <Icon name="User" size={14} color="white" />
              </div>
              <span className="text-sm">Profile</span>
              <Icon name="ChevronDown" size={14} />
            </Button>

            {isProfileOpen && (
              <div className="absolute top-full right-0 mt-2 w-56 bg-popover border border-border rounded-md shadow-elevated z-50">
                <div className="py-1">
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-sm font-medium text-popover-foreground">John Trader</p>
                    <p className="text-xs text-muted-foreground">john.trader@tradepulse.com</p>
                  </div>
                  <button className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left text-popover-foreground hover:bg-muted transition-colors duration-200">
                    <Icon name="Layout" size={16} />
                    <span>Saved Layouts</span>
                  </button>
                  <button className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left text-popover-foreground hover:bg-muted transition-colors duration-200">
                    <Icon name="Bell" size={16} />
                    <span>Alert Preferences</span>
                  </button>
                  <button className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left text-popover-foreground hover:bg-muted transition-colors duration-200">
                    <Icon name="Settings" size={16} />
                    <span>Settings</span>
                  </button>
                  <div className="border-t border-border mt-1 pt-1">
                    <button className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left text-error hover:bg-muted transition-colors duration-200">
                      <Icon name="LogOut" size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMobileMenuToggle}
            className="md:hidden"
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
          </Button>
        </div>
      </div>
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-card border-t border-border shadow-elevated">
          <div className="px-4 py-4 space-y-2">
            {[...navigationItems, ...moreItems]?.map(item => renderNavItem(item, true))}
            
            {/* Mobile Connection Status */}
            <div className="flex items-center gap-2 px-3 py-2 text-sm">
              <Icon 
                name={getConnectionIcon()} 
                size={16} 
                className={`${getConnectionColor()} ${connectionStatus === 'connecting' ? 'animate-pulse' : ''}`}
              />
              <span className={`font-medium ${getConnectionColor()}`}>
                Connection: {connectionStatus?.charAt(0)?.toUpperCase() + connectionStatus?.slice(1)}
              </span>
            </div>

            {/* Mobile Profile Section */}
            <div className="border-t border-border pt-4 mt-4">
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-foreground">John Trader</p>
                <p className="text-xs text-muted-foreground">john.trader@tradepulse.com</p>
              </div>
              <button className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left text-foreground hover:bg-muted transition-colors duration-200">
                <Icon name="Settings" size={16} />
                <span>Settings</span>
              </button>
              <button className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left text-error hover:bg-muted transition-colors duration-200">
                <Icon name="LogOut" size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;